<template>
  <div class="tw:flex tw:flex-col tw:max-w-full tw:md:max-w-[1044px] tw:mx-auto tw:w-full tw:px-4 tw:md:px-0">
    <!-- Back button -->
    <div class="tw:flex tw:items-center tw:gap-2 tw:mb-4 tw:cursor-pointer" @click="goBack">
      <IconGoogle name="arrow_back" size="14px" color="var(--maincolor)" />
      <TextPlay size="sm" :text="$t('live_lessons.back')" color="var(--text-color)" />
    </div>

    <!-- Header: status + time -->
    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:md:gap-6 tw:mb-4">
      <div
        v-if="liveLesson?.status"
        class="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-2 tw:rounded-lg tw:uppercase"
        :class="liveLesson?.status === 'LIVE' ? 'tw:bg-(--feedback-error-600)' : 'tw:bg-(--badge-bg-default)'"
      >
        <IconGoogle size="20px" name="live_tv" :color="liveLesson?.status === 'LIVE' ? 'white' : 'var(--fontcolor)'" />
        <TextPlay size="xs" weight="bold" :color="liveLesson?.status === 'LIVE' ? 'white' : 'var(--fontcolor)'" :text="checkLiveStatus(liveLesson?.status)" />
      </div>

      <div v-if="liveLesson?.status !== 'LIVE'" class="tw:flex tw:items-center tw:gap-3 tw:md:gap-6">
        <div class="tw:flex tw:items-center tw:gap-1">
          <IconGoogle size="16px" name="event" color="var(--maincolor)" />
          <TextPlay :skeleton="state.isLoading" color="var(--fontcolor)" size="sm" :text="formatTimeDate(liveLesson?.start_at)" />
        </div>
        <div class="tw:flex tw:items-center tw:gap-1">
          <IconGoogle size="16px" name="schedule" color="var(--maincolor)" />
          <TextPlay :skeleton="state.isLoading" color="var(--fontcolor)" size="sm" :text="formatTimeHour(liveLesson?.start_at)" />
        </div>
      </div>

      <ButtonPlayV2
        v-if="liveLesson?.status === 'SCHEDULED'"
        variant="filled"
        size="sm"
        class="tw:ml-auto"
        :loading="state.isLoading"
        :text="$t('live_lessons.live.update')"
        @click="getLiveLesson"
      />
    </div>

    <!-- Title -->
    <div class="tw:mb-4 tw:md:mb-8">
      <TextPlay :skeleton="state.isLoading" color="var(--fontcolor)" size="2xl" weight="bold" :text="liveLesson?.title" />
    </div>

    <!-- Cover / Player -->
    <div class="tw:mb-4 tw:md:mb-8">
      <img
        v-if="liveLesson?.status !== 'LIVE'"
        class="tw:w-full tw:aspect-video tw:object-cover tw:rounded-lg"
        :src="liveLesson?.cover?.cdn_url || '/waiting-live-default.png'"
      />
      <Player
        v-else-if="liveLesson?.status === 'LIVE' && liveLesson?.media_url"
        :media-type="liveLesson?.media_type?.toLocaleLowerCase()"
        :source="liveLesson?.media_url"
        :thumb="liveLesson?.cover?.cdn_url || '/waiting-live-default.png'"
      />
    </div>

    <!-- Tabs -->
    <TabsPlayV2
      v-if="!state.isLoading"
      v-model="state.tabSelected"
      :tabs="tabs"
    >
      <template #description>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="liveLesson?.content"
          class="tw:text-(--fontcolor) tw:text-base tw:leading-relaxed"
          v-html="liveLesson.content"
        />
        <!-- eslint-enable vue/no-v-html -->
        <TextPlay
          v-else
          size="sm"
          color="var(--text-color)"
          :text="$t('live_lessons.live.no_description')"
        />
      </template>
      <template #documents>
        <div v-if="liveLesson?.attachments?.length > 0" class="tw:flex tw:flex-wrap tw:gap-4">
          <div
            v-for="(doc, index) in liveLesson.attachments"
            :key="index"
            class="tw:flex tw:items-center tw:gap-3 tw:p-4 tw:bg-(--box-background-color) tw:rounded-lg tw:w-full tw:md:w-[400px]"
          >
            <inline-svg :src="typeSVG(doc)" width="20px" height="20px" />
            <div class="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
              <TextPlay :text="doc?.name" weight="semibold" size="sm" color="var(--fontcolor)" class="tw:truncate" />
              <TextPlay :text="doc?.size" size="xs" color="var(--text-color)" />
            </div>
            <IconGoogle color="var(--maincolor)" name="download" size="16px" class="tw:cursor-pointer" @click="downloadDocument(doc)" />
          </div>
        </div>
        <TextPlay
          v-else
          size="sm"
          color="var(--text-color)"
          :text="$t('live_lessons.live.no_documents')"
        />
      </template>
    </TabsPlayV2>
  </div>
</template>

<script setup lang="ts">
import { ButtonPlayV2 } from "@/components/atomic"
import InlineSvg from "vue-inline-svg";
import { toast } from "@/events";
import { useI18n } from "vue-i18n";
import { ref, reactive, computed } from "vue";
import { onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { LiveLesson, Media, play } from "@voomp/play-back";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const liveLesson = ref<LiveLesson>({
	status: 'DRAFT'
});

const state = reactive({
  isLoading: true,
  tabSelected: 'description',
});

const tabs = computed(() => {
	return [
		{
			id: "description",
			icon: "description",
			label: t("live_lessons.live.description"),
		},
		{
			id: "documents",
			icon: "folder",
			label: t("live_lessons.live.documents", { quantity: liveLesson.value?.attachments?.length || 0 }),
		},
	];
});

async function getLiveLesson() {
	state.isLoading = true;
	try {
		const response = await play.live_lesson.get_live_v2({
			uuid: route.params.id as string
		});
		liveLesson.value = response;
	} catch (_error) {
		toast.error(t("live_lessons.live.error_desc"), t("live_lessons.live.error"));
	} finally {
		state.isLoading = false;
	}
}

function checkLiveStatus(status: string) {
	const liveStatusMap = {
		DRAFT: "live_lessons.live.draft",
		LIVE: "live_lessons.live.live",
		SCHEDULED: "live_lessons.live.as_soon",
		FINISHED: "live_lessons.live.finished",
	};
	return t(liveStatusMap[status] || 'ERROR');
}


function formatTimeDate(date: string) {
	if (!date) return '';
	return new Date(date).toLocaleDateString('pt-BR');
}

function formatTimeHour(date: string) {
	if (!date) return '';
	return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function typeSVG(doc: Media) {
	const data = doc.url || doc.path;
	const extension = data.split(".").pop();

	const typeMap = {
		pdf: "PDF-LM",
		zip: "ZIP-LM",
		rar: "RAR-LM",
		xls: "XLS-LM",
		xlsx: "XLS-LM",
		ppt: "PPT-LM",
		pptx: "PPT-LM",
		doc: "DOC-LM",
		docx: "DOC-LM",
		csv: "XLS-LM",
		mp3: "MPEG-LM",
		mpeg: "MPEG-LM",
	};

	if (typeMap[extension]) {
		return import.meta.env.VUE_ASSETS_URL + `/images/file-types/${typeMap[extension]}.svg`;
	}
	return import.meta.env.VUE_ASSETS_URL + `/images/file-types/image_not_found.svg`;
}

function downloadDocument(doc) {
	const link = document.createElement("a");
	link.href = doc.url;
	link.download = doc.name;
	link.target = "_blank";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

function goBack() {
	router.push({ name: 'LiveLessons' });
}

onMounted(async () => {
	getLiveLesson();
});
</script>

